import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import SuperAdminLayout from '../components/SuperAdminLayout'
import LoadingSpinner from '../../../components/LoadingSpinner'

export default function SuperAdminClubs() {
  const [clubs, setClubs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({ name: '', subscription_plan: 'free' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const fetchClubs = async () => {
    const { data } = await supabase.from('clubs').select('*, chapters(count), supporters(count)').order('created_at', { ascending: false })
    if (data) setClubs(data)
    setLoading(false)
  }

  useEffect(() => { fetchClubs() }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    const { error } = await supabase.from('clubs').insert({ name: formData.name, subscription_plan: formData.subscription_plan })
    if (error) { setError(error.message); setSaving(false); return }
    setShowForm(false)
    setFormData({ name: '', subscription_plan: 'free' })
    fetchClubs()
    setSaving(false)
  }

  const inputClass = "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  if (loading) return <LoadingSpinner fullScreen />

  return (
    <SuperAdminLayout>
      <div className="mb-6 lg:mb-8 flex justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-4xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">Clubs</h1>
          <p className="text-gray-500 text-sm">{clubs.length} clubs on the platform</p>
        </div>
        <button onClick={() => setShowForm(true)} className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-3 font-bold uppercase tracking-widest text-xs sm:text-sm transition-colors whitespace-nowrap">
          Add Club
        </button>
      </div>

      <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-black/20">
              {['Club Name', 'Plan', 'Members', 'Chapters', 'Joined', 'Status'].map((h) => (
                <th key={h} className="text-left py-3 px-4 text-xs text-gray-500 uppercase tracking-widest font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clubs.map((club) => (
              <tr key={club.id} className="border-b border-gray-200 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                <td className="py-3 px-4 font-bold text-gray-900 dark:text-white whitespace-nowrap">{club.name}</td>
                <td className="py-3 px-4">
                  <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">{club.subscription_plan}</span>
                </td>
                <td className="py-3 px-4 text-gray-500">{club.supporters?.[0]?.count || 0}</td>
                <td className="py-3 px-4 text-gray-500">{club.chapters?.[0]?.count || 0}</td>
                <td className="py-3 px-4 text-gray-500 text-xs whitespace-nowrap">
                  {new Date(club.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                <td className="py-3 px-4"><span className="text-xs font-bold uppercase tracking-widest text-green-500">Active</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 p-6 sm:p-8 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-black text-xl uppercase tracking-tight text-gray-900 dark:text-white">Add New Club</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">✕</button>
            </div>
            {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">{error}</div>}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Club Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Club Name" required className={inputClass} />
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Subscription Plan</label>
                <select value={formData.subscription_plan} onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value })} className={inputClass}>
                  <option value="free">Free</option>
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="flex gap-3 mt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/30 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors">{saving ? 'Creating...' : 'Create Club'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  )
}